import { useState } from "react"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { Link } from "react-router-dom"
import { isEmail } from "utils/validation"
import EmailInput from "components/inputs/emailInput"
import Button from "components/inputs/button"
import ProcessingButton from "components/inputs/processingButton"
import useLayout from "hooks/useLayout"
import useAuth from "hooks/useAuth"
import Form from "components/forms/form"
import { ReactComponent as MailLockIcon } from "assets/icons/mail_lock.svg"
import { ReactComponent as LoginIcon } from "assets/icons/login.svg"
import { InputMaxLength } from "types"

const LostPasswordForm = () => {

    const { t } = useTranslation()

    // Form data.
    const [email, setEmail] = useState('')
    const [isValid, setIsValid] = useState(false)

    // Form status.
    const [success, setSuccess] = useState(false)
    const [errors, setErrors] = useState<string[]>([])

    // Auth hooks.
    const { recoverPassword, isRecoveringPassword } = useAuth()

    // Layout hooks.
    const { addToast } = useLayout()

    // Handle submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isValid) return
        setErrors([])
        
        try {
            await recoverPassword({ email });
            setSuccess(true)
            addToast({ text: t('toastPasswordRecoverySuccess'), colorScheme: 'green' })
        } catch (error: any) {
            const errorMessage = decodeFirebaseError(error?.code || error?.message || 'Unknown error')
            setErrors([errorMessage])
            addToast({ text: t('toastPasswordRecoveryError'), colorScheme: 'red' })
        }
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetVal = e.target.value
        setEmail(targetVal)
        // Check if email is valid.
        if (isEmail(targetVal)) {
            setIsValid(true)
        } else {
            setIsValid(false)
        }
    }

    // Catch success.
    if (success) {
        return (
            <>
                <p>{t('passwordRecoveryEmailSent')}</p>
                <Link className="block w-full md:inline-block md:w-fit" to="/login"><Button wideOnMobile design="primary"><LoginIcon /><span>{t('login')}</span></Button></Link>
            </>
        )
    }

    // Return the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isRecoveringPassword}>
            <p>{t('lostPasswordInstructions')}</p>
            <EmailInput label={t('email')} value={email} onChange={handleEmailChange} required maxLength={InputMaxLength.medium} />
            {errors.length > 0 && <FormErrors errors={errors} />}
            {isRecoveringPassword ? (
                <ProcessingButton design="primary" />
            ) : (
                <Button wideOnMobile type="submit" design="primary"><MailLockIcon /><span>{t('recoverPassword')}</span></Button>
            )}
        </Form>
    )
}

export default LostPasswordForm


